<template>
  <div class="hike-item">
    <div class="hike-content d-flex">
      <!-- Thumbnail with play button -->
      <div class="hike-thumbnail-wrapper">
        <img :src="thumbnailUrl" class="hike-thumbnail" :alt="$t('recorded_trails.recorded_trail_item.recorded_trail_thumbnail')" />

        <!-- Selection mode checkbox -->
        <div v-if="selectionMode" class="selection-checkbox-wrapper" @click.stop="emit('selection-changed', trail.id)">
          <div class="selection-checkbox" :class="{ selected: isSelected }">
            <i v-if="isSelected" class="material-icons">check</i>
          </div>
        </div>

        <button v-if="!selectionMode" class="play-button" @click="onShowFlyover">
          <i class="material-icons">play_circle</i>
        </button>
      </div>

      <!-- Trail info -->
      <div class="hike-info">
        <!-- Private badge -->
        <span v-if="trail.public === false" class="private-badge" :title="$t('recorded_trails.recorded_trail_item.private_recording')">
          <i class="material-icons">lock</i>
        </span>

        <p class="mb-0">
          <a :href="trail.shareableUrl" class="hike-name-link">
            <strong class="trail-title">{{ trail.title || $t('recorded_trails.recorded_trail_item.untitled_recorded_trail') }}</strong>
          </a>
        </p>
        <p class="text-muted mb-0"><small style="font-size: .75em;">{{ formattedDate }}</small></p>
        <p v-if="trail.connectedHike" class="connected-hike mb-0">
          <a :href="trail.connectedHike.shareableUrl" class="connected-hike-link">
            <i class="material-icons">hiking</i>
            <span>{{ trail.connectedHike.name }}</span>
          </a>
        </p>
      </div>

      <!-- Stats -->
      <div class="hike-stats d-flex gap-3">
        <div class="stat">
          <span class="stat-label">{{ $t('shared.distance') }}</span>
          <p>{{ formattedDistance }}</p>
        </div>
        <div class="stat">
          <span class="stat-label">{{ $t('shared.duration') }}</span>
          <p>{{ formattedDuration }}</p>
        </div>
        <div class="stat">
          <span class="stat-label">{{ $t('shared.elevation') }}</span>
          <p>{{ formattedElevation }}</p>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="hike-actions d-flex align-items-center gap-2">
        <button class="btn btn-icon btn-yellow" @click="onExportGpx">
          <span class="material-icons text-dark p-1 d-block" style="font-size: 28px;">arrow_circle_down</span>
        </button>

        <!-- More options dropdown -->
        <div class="dropdown">
          <button class="btn btn-icon dropdown-toggle" type="button" data-bs-toggle="dropdown">
            <i class="material-icons">more_vert</i>
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li><a class="dropdown-item" :href="trail.shareableUrl + '/map'">{{ $t('shared.view_map') }}</a></li>
            <li><button class="dropdown-item" @click="onExportGpx">{{ $t('recorded_trails.recorded_trail_item.export_gpx') }}</button></li>
            <li><button class="dropdown-item" @click="onShowFlyover">{{ $t('shared.3d_flyover') }}</button></li>
            <li><a class="dropdown-item" :href="printUrl">{{ $t('shared.print_map') }}</a></li>
            <li v-if="!isPublicView"><button class="dropdown-item" @click="onEditTrail">{{ $t('recorded_trails.recorded_trail_item.edit_details') }}</button></li>
            <li v-if="!isPublicView"><button class="dropdown-item" @click="onCropTrail">{{ $t('recorded_trails.recorded_trail_item.crop_recording') }}</button></li>
            <li><button class="dropdown-item" @click="onCopyShare">{{ $t('recorded_trails.recorded_trail_item.share_route') }}</button></li>
            <li v-if="!isPublicView"><button class="dropdown-item text-danger" @click="onDeleteTrail">{{ $t('recorded_trails.recorded_trail_item.delete_recording') }}</button></li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Trail photos if available -->
    <div v-if="trail.images && trail.images.length" class="trail-photos mt-2">
      <div class="row g-1">
        <div v-for="(img, i) in trail.images" :key="img.id || i" class="col-4 col-md-2 p-1">
          <div class="trail-photo-container">
            <img
              v-if="photoSrc(img)"
              :src="photoSrc(img)"
              class="trail-photo"
              loading="lazy"
              :alt="$t('recorded_trails.recorded_trail_item.recording_photo')"
              role="button"
              @click="openViewer(i)"
            />
            <div v-else class="trail-photo-processing">
              <span class="spinner-border spinner-border-sm" role="status"></span>
              <small>{{ $t('recorded_trails.recorded_trail_item.processing') }}</small>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Photo viewer modal -->
    <Teleport to="body">
      <div v-if="viewerOpen" class="photo-viewer-overlay" @click="closeViewer">
        <button class="photo-viewer-close" @click.stop="closeViewer" :aria-label="$t('shared.close')">
          <i class="material-icons">close</i>
        </button>
        <button
          v-if="trail.images.length > 1"
          class="photo-viewer-arrow photo-viewer-arrow--left"
          :class="{ 'photo-viewer-arrow--disabled': viewerIndex === 0 }"
          @click.stop="prevPhoto"
          :aria-label="$t('recorded_trails.recorded_trail_item.previous_photo')"
        >
          <i class="material-icons">chevron_left</i>
        </button>
        <div class="photo-viewer-content" @click.stop>
          <img
            v-if="viewerSrc(trail.images[viewerIndex])"
            :src="viewerSrc(trail.images[viewerIndex])"
            :alt="$t('recorded_trails.recorded_trail_item.recording_photo_number', { number: viewerIndex + 1 })"
          />
          <div v-else class="photo-viewer-processing">
            <span class="spinner-border spinner-border-sm" role="status"></span>
            <small>{{ $t('recorded_trails.recorded_trail_item.processing_2') }}</small>
          </div>
          <div v-if="trail.images.length > 1" class="photo-viewer-counter">
            {{ viewerIndex + 1 }} / {{ trail.images.length }}
          </div>
        </div>
        <button
          v-if="trail.images.length > 1"
          class="photo-viewer-arrow photo-viewer-arrow--right"
          :class="{ 'photo-viewer-arrow--disabled': viewerIndex === trail.images.length - 1 }"
          @click.stop="nextPhoto"
          :aria-label="$t('recorded_trails.recorded_trail_item.next_photo')"
        >
          <i class="material-icons">chevron_right</i>
        </button>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import moment from 'moment/moment'
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref'
import { resolveViewerSrc, clampPrevIndex, clampNextIndex } from '../../../utils/helpers/photoViewerNav'

const props = defineProps({
  trail: { type: Object, required: true },
  isPublic: { type: Boolean, default: false },
  selectionMode: { type: Boolean, default: false },
  isSelected: { type: Boolean, default: false }
})

const emit = defineEmits(['show-flyover', 'edit-trail', 'export-gpx', 'copy-share', 'crop-trail', 'delete-trail', 'selection-changed'])
const isPublicView = computed(() => !!props.isPublic)

const printUrl = computed(() => `/print-maps?data_ids[]=${props.trail?.id}&data_types[]=Recording`)

const thumbnailUrl = computed(() => {
  // Use thumbnail URL if available, otherwise use fallback
  if (props.trail?.thumbnailUrl) {
    return props.trail.thumbnailUrl
  }
  // Fallback to default recording thumbnail
  return new URL('../../../images/static_map_thumbnail.webp', import.meta.url).href
})

// Photo thumbnail source. Use a resized variant (sm → md) once it has been
// processed; while variants are still generating, show the "Processing…"
// placeholder rather than the full-size original — serving the original here is
// exactly the heavy/uncompressed-thumbnail bug we're fixing.
function photoSrc(img) {
  const file = img?.file
  if (!file || !file.processed) return null
  return file.smUrl || file.mdUrl || file.url
}

// Full-resolution source for the photo viewer modal — prefer the largest
// available variant rather than the sm/md ones used for grid thumbnails.
function viewerSrc(img) {
  return resolveViewerSrc(img?.file)
}

const viewerOpen = ref(false)
const viewerIndex = ref(0)

function openViewer(index) {
  viewerIndex.value = index
  viewerOpen.value = true
}
function closeViewer() {
  viewerOpen.value = false
}
function prevPhoto() {
  viewerIndex.value = clampPrevIndex(viewerIndex.value)
}
function nextPhoto() {
  viewerIndex.value = clampNextIndex(viewerIndex.value, props.trail.images.length)
}
function onViewerKeydown(e) {
  if (!viewerOpen.value) return
  if (e.key === 'Escape') closeViewer()
  else if (e.key === 'ArrowLeft') prevPhoto()
  else if (e.key === 'ArrowRight') nextPhoto()
}

onMounted(() => window.addEventListener('keydown', onViewerKeydown))
onUnmounted(() => window.removeEventListener('keydown', onViewerKeydown))

const formattedDate = computed(() => moment(props.trail?.createdAt).format('LLLL'))

const formattedDistance = computed(() => {
  const value = props.trail?.distanceMeters
  if (!value && value !== 0) return '—'
  return MeasurementPrefUtility.preference().smallUnitFormatter(value, true)
})

const formattedElevation = computed(() => {
  const value = props.trail?.elevationGainMeters
  if (!value && value !== 0) return '—'
  return MeasurementPrefUtility.preference().smallUnitFormatter(value, false)
})

const formattedDuration = computed(() => {
  const duration = props.trail?.durationSeconds ?? props.trail?.elapsedSeconds
  if (!duration && duration !== 0) return '—'

  const hours = Math.floor(duration / 3600)
  const minutes = Math.floor((duration % 3600) / 60)

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
})

const difficultyClass = computed(() => 'difficulty-medium')

function onShowFlyover(e) {
  // Pass the recording's share config so the preview's in-flyover "Share video" button works — it has
  // no page-level data-* element to read from here (that only exists on the recording overview page).
  emit('show-flyover', e, props.trail, {
    recordingId: String(props.trail?.id ?? ''),
    shareUrl: props.trail?.shareableUrl || '',
    title: props.trail?.title || '',
    distance: formattedDistance.value,
    elevation: formattedElevation.value,
    duration: formattedDuration.value,
    thumbnailUrl: thumbnailUrl.value,
  })
}
function onEditTrail() { if (isPublicView.value) return; emit('edit-trail', props.trail) }
function onExportGpx() { emit('export-gpx', props.trail) }
function onCopyShare(e) { emit('copy-share', e, props.trail) }
function onCropTrail() { if (isPublicView.value) return; emit('crop-trail', props.trail) }
function onDeleteTrail() { if (isPublicView.value) return; emit('delete-trail', props.trail) }

</script>

<style scoped>
.hike-item {
  background: #1f2026;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.2s;
  margin-bottom: 1rem;
}

.hike-item:hover {
  background: #252530;
  border-color: rgba(255, 255, 255, 0.12);
}

.hike-content {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  height: 120px;
}

.hike-thumbnail-wrapper {
  position: relative;
  width: 160px;
  height: -webkit-fill-available;
  background: var(--gray);
  flex: 0 0 15%;
  overflow: hidden;
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
}

.hike-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-button {
  position: absolute;
  top: .5em;
  left: .5em;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  cursor: pointer;
  transition: all 0.2s;
  backdrop-filter: blur(4px);
}

.play-button:hover {
  background: rgba(0, 0, 0, 0.9);
}

.play-button i {
  font-size: 20px;
}

/* Hike Info */
.hike-info {
  flex: 0 0 23%;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  max-width: 220px;
}

.difficulty-badge {
  display: inline-block;
  padding: 2px 5px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  text-transform: capitalize;
  letter-spacing: 0.5px;
  align-self: flex-start;
}

.difficulty-easy { background: #4BC263; color: var(--dark); }
.difficulty-medium { background: #F57700; color: var(--dark); }
.difficulty-hard { background: #DB2B35; color: #fff; }
.difficulty-extra { background: #8B008B; color: #fff; }

.private-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}

.private-badge i {
  font-size: 12px;
}

.hike-name-link { color: #fff; text-decoration: none; transition: text-decoration 0.2s; }
.hike-name-link:hover { color: #fff; text-decoration: underline; }

.connected-hike { margin-top: 0.25rem; }
.connected-hike-link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #FBCD01;
  text-decoration: none;
  font-size: 0.75em;
  transition: color 0.2s;
}
.connected-hike-link:hover { color: #e6ba00; text-decoration: underline; }
.connected-hike-link i { font-size: 14px; }

.trail-title {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* Stats */
.hike-stats { display: flex; flex: 0 0 32%; gap: 3rem; margin-top: 0.75rem; }

.stat { display: flex; flex-direction: column; gap: 0.25rem; }
.stat-label { font-size: .5em; letter-spacing: 0.5px; color: #a6a6a6; }
.stat-value { color: #fff; font-size: 15px; font-weight: 600; }

/* Action Buttons */
.hike-actions { display: flex; align-items: center; padding-right: 1em; gap: 0.75rem; margin-left: auto; }
.btn-icon { background: transparent; border: 1px solid rgba(255, 255, 255, 0.15); color: rgba(255, 255, 255, 0.7); width: 36px; height: 36px !important; border-radius:100%; display: flex; align-items: center; justify-content: center; padding: 0; transition: all 0.2s; cursor: pointer; }
.btn-icon:hover { background: rgba(255, 255, 255, 0.05); border-color: rgba(255, 255, 255, 0.25); color: #fff; }
.btn-icon i { font-size: 20px; }
.btn-yellow { background: #FBCD01; color: #000; border: none; }
.btn-yellow:hover { background: #e6ba00; }
.btn-yellow i { color: #000; }

/* Dropdowns */
.dropdown-toggle::after { display: none; }
.dropdown-menu { background: #252530; border: 1px solid rgba(255, 255, 255, 0.1); box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4); border-radius: 8px; padding: 0.5rem; margin-top: 0.5rem; }
.dropdown-item { color: rgba(255, 255, 255, 0.9); padding: 0.5rem 1rem; border-radius: 6px; font-size: 14px; }
.dropdown-item:hover { background: rgba(255, 255, 255, 0.08); color: #fff; }

/* Trail photos */
.trail-photos {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 0.5rem;
}

.trail-photo-container {
  width: 100%;
  height: 80px;
  overflow: hidden;
  border-radius: 6px;
}

.trail-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
}

.trail-photo-processing {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.7rem;
}

/* Photo viewer modal */
.photo-viewer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.photo-viewer-content {
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
}

.photo-viewer-content img {
  max-width: 90vw;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
}

.photo-viewer-processing {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  padding: 3rem;
}

.photo-viewer-counter {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.85rem;
}

.photo-viewer-close,
.photo-viewer-arrow {
  position: fixed;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2001;
  transition: background 0.2s;
}

.photo-viewer-close:hover,
.photo-viewer-arrow:hover {
  background: rgba(255, 255, 255, 0.2);
}

.photo-viewer-close {
  top: 1rem;
  right: 1rem;
  width: 40px;
  height: 40px;
}

.photo-viewer-arrow {
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
}

.photo-viewer-arrow--left { left: 1rem; }
.photo-viewer-arrow--right { right: 1rem; }
.photo-viewer-arrow--disabled { opacity: 0.3; pointer-events: none; }

@media (max-width: 576px) {
  .photo-viewer-arrow { width: 40px; height: 40px; }
  .photo-viewer-arrow--left { left: 0.5rem; }
  .photo-viewer-arrow--right { right: 0.5rem; }
}

/* Selection Mode Checkbox */
.selection-checkbox-wrapper {
  position: absolute;
  top: 0.5em;
  left: 0.5em;
  cursor: pointer;
  z-index: 10;
}

.selection-checkbox {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.6);
  border: 2px solid rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  backdrop-filter: blur(4px);
}

.selection-checkbox:hover {
  background: rgba(0, 0, 0, 0.8);
  border-color: rgba(255, 255, 255, 0.8);
}

.selection-checkbox.selected {
  background: #FBCD01;
  border-color: #FBCD01;
}

.selection-checkbox i {
  font-size: 20px;
  color: #000;
}

/* ---------------- Responsive Tweaks ---------------- */
@media (max-width: 992px) {
  .hike-content {
    flex-direction: column;
    align-items: stretch;
    height: auto;
    padding: 0.75rem;
    gap: 0.75rem;
  }

  .hike-thumbnail-wrapper {
    width: 100%;
    height: 180px;
    flex: none;
  }

  .hike-info {
    flex: none;
    padding: 0 .25rem;
  }

  .hike-stats {
    flex: none;
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.75rem;
    margin-top: 0;
  }

  .hike-actions {
    flex: none;
    justify-content: flex-start;
    padding: 0 .25rem .5rem;
    margin-left: 0;
  }

  .btn-icon { width: 34px; height: 34px !important; }
}

@media (max-width: 576px) {
  .hike-thumbnail-wrapper { height: 140px; }
  .hike-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem; }
  .btn-icon { width: 32px; height: 32px !important; }
}
</style>
